import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { WorkoutSessionDisplayData } from '../types/workoutLapData'

interface UseWorkoutSessionDataReturn {
  workoutSessionData: WorkoutSessionDisplayData | undefined
  loading: boolean
  error: string | null
  refreshWorkoutSessionData: () => Promise<void>
}

export const useWorkoutSessions = (workoutId: string): UseWorkoutSessionDataReturn => {
  const [workoutSessionData, setWorkoutSessionData] = useState<WorkoutSessionDisplayData | undefined>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const loadSessionData = useCallback(async () => {
    if (!workoutId) return

    try {
      setLoading(true)
      setError(null)

      const { data, error: supabaseError } = await supabase
        .from('workout_sessions')
        .select(`
          *,
          workout_laps!workout_session_id (
            id,
            lap_number,
            start_time,
            total_timer_time,
            total_elapsed_time,
            total_distance,
            total_work,
            avg_power,
            avg_heart_rate,
            avg_speed,
            intensity
          )
        `)
        .eq('workout_id', workoutId)

      if (supabaseError) {
        throw new Error(supabaseError.message)
      }

      const displayData = {
        ...data?.[0],
        workout_laps: data?.[0]?.workout_laps || [],
      } as WorkoutSessionDisplayData
      setWorkoutSessionData(displayData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load lap data')
      console.error('Error loading lap data:', err)
    } finally {
      setLoading(false)
    }
  }, [workoutId])

  const refreshWorkoutSessionData = useCallback(async () => {
    await loadSessionData()
  }, [loadSessionData])

  // Load lap data on mount and when workoutId changes
  useEffect(() => {
    if (workoutId) {
      loadSessionData()
    }
  }, [workoutId, loadSessionData])

  return {
    workoutSessionData,
    loading,
    error,
    refreshWorkoutSessionData,
  }
}

// Helper function to calculate compliance based on power vs target
const calculateCompliance = (avgPower?: number | null, targetPower?: number | null): 'good' | 'warning' | 'poor' => {
  if (!avgPower || !targetPower) return 'good'

  const powerDiff = Math.abs(avgPower - targetPower) / targetPower

  if (powerDiff > 0.15) return 'poor'
  if (powerDiff > 0.08) return 'warning'
  return 'good'
}
